import { API_BASE } from '@/app/config';

interface IParticipantResponse {
  is_host: boolean;
  screen_share_id: string;
  user_id: string;
  user_name: string;
}

export interface MeetingDataResponse {
  token: string;
  screen_share_token: string;
  meeting_data: {
    channel_name: string;
    house_address: string;
    is_host: boolean;
    appointment_start: string | null;
    screen_share_id: string;
    user_id: string;
    user_name: string;
    meeting_urn: string;
    review_urls: string[];
  };
  participants: IParticipantResponse[];
}

export class MeetingFetchError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'MeetingFetchError';
    this.status = status;
  }
}

interface MeetingPreload {
  hash: string;
  response: Promise<Response>;
}

type PreloadingWindow = Window & { __meetingPreload?: MeetingPreload };

// index.html fires this exact request the moment the page arrives, so the
// answer downloads in parallel with the bundle. Claiming is one-shot: a
// Response body reads once, and a retry must go back to the network.
function claimPreloadedResponse(hash: string) {
  const preload = (window as PreloadingWindow).__meetingPreload;
  if (preload?.hash !== hash) {
    return undefined;
  }
  delete (window as PreloadingWindow).__meetingPreload;
  return preload.response;
}

export default async function fetchMeeting(hash: string) {
  // A bare GET stays a "simple" CORS request: no Content-Type header means
  // no OPTIONS preflight ahead of it, on the preload and the fallback alike.
  const response = await (claimPreloadedResponse(hash) ?? fetch(`${API_BASE}/meeting/${hash}`));

  if (!response.ok) {
    throw new MeetingFetchError(
      `Failed to fetch meeting: ${response.statusText}`,
      response.status,
    );
  }

  const data = await response.json() as MeetingDataResponse;

  const participants = data.participants.map((participant) => ({
    ...participant,
    user_id: parseInt(participant.user_id, 10),
    screen_share_id: parseInt(participant.screen_share_id, 10),
  }));

  return {
    ...data,
    meeting_data: {
      ...data.meeting_data,
      // Recording will not be properly mapped if the next values are of type string.
      user_id: parseInt(data.meeting_data.user_id, 10),
      screen_share_id: parseInt(data.meeting_data.screen_share_id, 10),
    },
    participants,
  };
}
